import React, { ReactElement, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './Login.less';
import { Button } from 'antd';
import { useAuth } from '@hooks/useAuth';
import { ErrorPage500 } from '@pages/ErrorPage';
import { IGNORE_NON_LTI_LOGIN_ERROR_KEY } from '@config/constants';
import { LogoIcon } from '@icons';

interface LtiLoginProps {
  loader: ReactElement;
}

function LtiErrorCard({
  emoji,
  title,
  children
}: {
  emoji: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="lti-error-page">
      <div className="lti-error-card">
        <LogoIcon className="lti-error-logo" />
        <div className="lti-error-emoji">{emoji}</div>
        <h1 className="lti-error-title">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export default function LtiLogin({ loader }: LtiLoginProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth(false, 'internal');
  const error = searchParams.get('error');
  const loginHint = searchParams.get('login_hint');
  const returnTo = searchParams.get('r') || '/';
  const { login } = useAuth(false, 'internal');

  const isError = !!error || !loginHint;

  useEffect(() => {
    if (isError) return;
    login({
      authorizationParams: {
        connection: 'lti',
        login_hint: loginHint
      },
      appState: { returnTo }
    });
  }, [isError, loginHint, login, returnTo]);

  if (!isError) return loader;

  if (error === 'non_lti_login') {
    const currentProvider = user?.providerId;
    const providerLabel =
      currentProvider === 'google-oauth2'
        ? 'Google'
        : currentProvider === 'auth0'
        ? 'password-based'
        : null;
    return (
      <LtiErrorCard emoji="🎒" title="Login method mismatch">
        <p className="lti-error-body">
          {providerLabel ? (
            <>
              You&apos;re signed in with your <strong>{providerLabel}</strong>{' '}
              account, but StreamWork needs to be opened through your learning
              platform so we can link you to the right course.
            </>
          ) : (
            <>
              StreamWork needs to be opened through your learning platform so we
              can link you to the right course.
            </>
          )}
        </p>
        <p className="lti-error-body">
          For the best experience, please log in to your learning platform (like
          Canvas or Moodle), navigate to your current course, and click the{' '}
          <strong>StreamWork</strong> link to get started.
          {providerLabel ? (
            <>
              {' '}
              You can also continue with your {providerLabel} account, but some
              course features may not be available.
            </>
          ) : (
            <>
              {' '}
              You can also continue without linking, but some course features
              may not be available.
            </>
          )}
        </p>
        <Button
          type="dashed"
          className="lti-error-continue-btn"
          onClick={() => {
            localStorage.setItem(IGNORE_NON_LTI_LOGIN_ERROR_KEY, 'true');
            navigate(returnTo);
          }}
        >
          Continue anyway
        </Button>
        <div className="lti-error-footer">
          Or close this window and open StreamWork through your learning
          platform.
        </div>
      </LtiErrorCard>
    );
  }

  if (error === 'direct_login' || (!error && !loginHint)) {
    return (
      <LtiErrorCard emoji="🎒" title="Head back to your classroom">
        <p className="lti-error-body">
          StreamWork needs to be opened through your learning platform so we can
          link you to the right course.
        </p>
        <p className="lti-error-body">
          Please log in to your learning platform (like Canvas or Moodle),
          navigate to your current course, and click the{' '}
          <strong>StreamWork</strong> link to get started.
        </p>
        <div className="lti-error-footer">
          You can safely close this window.
        </div>
      </LtiErrorCard>
    );
  }

  return <ErrorPage500 />;
}
